import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Divider, Text } from '@chakra-ui/react';
import { useParams, useSearchParams } from 'react-router-dom';
import ReactPixel from 'react-facebook-pixel';
import ReactGA4 from 'react-ga4';
import { useQuery } from '@tanstack/react-query';

import { PaymentType, CreditCardBrand } from 'src/types/dto';

import { useMeQuery } from 'src/hooks/queries/user';

import { useEventContext } from '../../provider';
import { useEventData, useReferral } from '../../hooks';
import {
  useCartStore,
  useCheckoutFormValuesStore,
  useCheckoutStore,
  useDiscountStore,
  usePrivateLinkStore,
} from '../../stores';
import { CheckoutSummary } from '../_components/CheckoutSummary';
import { putSellBuy } from '../../api';
import { getCartItems } from '../../utils';

import {
  ErrorMessage,
  FloatingLabel,
  FloatingLabelContainer,
  FloatingLabelInputNumberFormat,
} from 'src/components/Inputs/FloatingLabel';
import ScrollToTop from 'src/components/ScrollToTop';
import { OwnersSection } from './OwnersSection';
import { PaymentMethodButton } from './PaymentMethodButton';
import { InstallmentsField } from './InstallmentsField';
import { cpf } from 'cpf-cnpj-validator';
import useCellphoneValidation from '../hooks/useCellphoneValidate';

const CheckoutPayment = () => {
  const { eventID } = useParams();
  const [searchParams] = useSearchParams();
  const coupon = searchParams.get('coupon');
  const privateLinkName = searchParams.get('linkname')
  const meQuery = useMeQuery();
  const { invalidFieldsNames, setInvalidFieldsNames } = useEventContext();
  const { getEventCart, getEventCartTotal, getEventCartTotalTickets } =
    useCartStore();
  const { getEventCheckout } = useCheckoutStore();
  const checkout = getEventCheckout(eventID!);
  const { formValues, setFormValues } = useCheckoutFormValuesStore();
  const { discount, useApplyMutation } = useDiscountStore();
  const { fetchEventInfo } = usePrivateLinkStore();
  const event = useEventData();
  const referral = useReferral(eventID!);

  const registerBuyQuery = useQuery({
    queryKey: ['sell/buy', checkout.sellId],
    queryFn: async () =>
      await putSellBuy({
        sellId: checkout.sellId,
        payload: {
          paymentType:
            formValues.selectedPaymentType !== PaymentType.Gratuito
              ? formValues.selectedPaymentType
              : PaymentType.Pix,
        },
        params: { ref: referral },
      }),

    enabled: !!(
      checkout.sellId &&
      formValues.selectedPaymentType &&
      meQuery.data?.email
    ),
  });

  const buyerInfo = registerBuyQuery.data?.buyerInfo;

  const checkoutBag = useMemo(
    () =>
      getCartItems({
        cart: getEventCart(eventID!),
        coupon: discount,
      })?.reduce(
        (acc, item) => ({
          totalCreditCardFee: acc.totalCreditCardFee + item.creditCardFee,
          // totalPixFee:
          //   acc.totalPixFee + (registerBuyQuery.data?.feePricePix ?? 0),
          totalTickets: acc.totalTickets + item.total,
          totalDiscount: acc.totalDiscount + item.discount,
          isCouponApplied: item.isCouponApplied || acc.isCouponApplied,
        }),
        {
          totalCreditCardFee: 0,
          // totalPixFee: 0,
          totalTickets: 0,
          totalDiscount: 0,
          isCouponApplied: false,
        },
      ),
    [discount, eventID, getEventCart],
  );

  const [totalInstallmentAmount, setTotalAmount] = useState(
    checkoutBag.isCouponApplied && !!discount
      ? checkoutBag.totalTickets +
          checkoutBag.totalCreditCardFee -
          checkoutBag.totalDiscount
      : checkoutBag.totalTickets + checkoutBag.totalCreditCardFee,
  );

  useEffect(() => {
    if (event?.integrations) {
      if (
        event.integrations.pixelId &&
        window.location.href.includes(event.id)
      ) {
        ReactPixel.init(event.integrations.pixelId);
        ReactPixel.trackSingle(event.integrations.pixelId, 'PageView');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventID]);

  // ANALITYCS => ReactPixel && checkoutReactGA4 && ReactGA4
  useEffect(() => {
    if (event) {
      const totalTickets = getEventCartTotalTickets(event.id);
      const contents = {
        id: event.id,
        quantity: totalTickets,
      };

      if (
        event?.integrations?.pixelId &&
        window.location.href.includes(event.id)
      ) {
        ReactPixel.trackSingle(
          event.integrations?.pixelId,
          'InitiateCheckout',
          {
            content_type: 'product',
            content_ids: [event.id],
            value: getEventCartTotal(event.id),
            contents: contents,
            num_items: totalTickets,
            content_name: event.name,
            currency: 'BRL',
          },
        );
      }

      const checkoutReactGA4 = {
        item_category: 'product',
        item_list_id: event.id,
        quantity: totalTickets,
        item_name: event.name,
      };

      ReactGA4.event('begin_checkout', {
        currency: 'BRL',
        value: getEventCartTotal(event.id),
        items: [checkoutReactGA4],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, getEventCartTotal, getEventCartTotalTickets]);

  // força uma nova chamada na api para corrigir os cálculos
  const { mutate } = useApplyMutation();

  useEffect(() => {
    if (!!(checkout.sellId && coupon && meQuery.data?.email)) {
      mutate({
        sellId: checkout.sellId,
        eventId: eventID!,
        couponName: coupon,
      });
    }
  }, [checkout.sellId, coupon, eventID, meQuery.data?.email, mutate]);

  useEffect(() => {
    if (eventID && privateLinkName) {
      fetchEventInfo(eventID, privateLinkName);
    }
  }, [eventID, privateLinkName, fetchEventInfo]);

  // DEFINE SE O DOCUMENTO E CELULAR SÃO OBRIGATÓRIOS
  // DEFINE O VALOR TOTAL BASEADO OU EM PARCELAS DO CARTAO DE CREDITO OU EM DESCONTO DO PIX
  useEffect(() => {
    if (
      registerBuyQuery.isSuccess &&
      !registerBuyQuery.isFetching &&
      registerBuyQuery.data
    ) {
      setFormValues({
        isDocumentRequired: registerBuyQuery.data.requireDocument,
        isPhoneRequired: registerBuyQuery.data.requirePhone,
        sellId: checkout.sellId
      });
      if (formValues.selectedPaymentType === PaymentType.CreditCard) {
        const installment =
          registerBuyQuery.data.installments?.find(
            installment => installment.installment === formValues.installments,
          ) || registerBuyQuery.data.installments?.[0];
        if (installment) setTotalAmount(installment.total);
      } else if (formValues.selectedPaymentType === PaymentType.Pix) {
        const total =
          checkoutBag.totalTickets + (registerBuyQuery.data?.feePricePix ?? 0);
        setTotalAmount(
          checkoutBag.isCouponApplied && !!discount
            ? total - checkoutBag.totalDiscount
            : total,
        );
      }
    }

    if (
      checkoutBag.totalTickets !== 0 &&
      (!formValues.selectedPaymentType ||
        formValues.selectedPaymentType === PaymentType.Gratuito)
    ) {
      setFormValues({
        selectedPaymentType: PaymentType.Pix,
      });
    }

    if (
      checkoutBag.totalTickets === 0 &&
      formValues.selectedPaymentType !== PaymentType.Gratuito
    ) {
      setFormValues({
        selectedPaymentType: PaymentType.Gratuito,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkoutBag.isCouponApplied,
    checkoutBag.totalDiscount,
    checkoutBag.totalCreditCardFee,
    checkoutBag.totalTickets,
    discount,
    formValues.installments,
    registerBuyQuery.data,
    registerBuyQuery.isFetching,
    registerBuyQuery.isSuccess,
  ]);

  const containerRef = useRef<HTMLDivElement>(null);

  const doesFormHasAnyError = invalidFieldsNames.length > 0;

  const isValidCpf = (typedCpf: string) => {
    typedCpf = typedCpf.replace(/[^\d]/g, '');

    if (cpf.isValid(typedCpf)) {
      return true;
    }
    return false;
  };

  const isValidPhone = useCellphoneValidation()

  const getErrorKey = (value: string) => {
    if (invalidFieldsNames?.includes(value)) {
      return true;
    }
    return false;
  };

  // GAMBIARRA
  // Scrolla para cima ou não escrolar dependendo do erro.
  useEffect(() => {
    if (!doesFormHasAnyError) return;
    if (!containerRef) return;

    const container = containerRef.current;
    const containerParent = container?.parentNode;
    const scrollableElement = containerParent?.parentElement;

    if (doesFormHasAnyError) {
      scrollableElement?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [invalidFieldsNames, doesFormHasAnyError, containerRef]);

  return (
    <Box
      ref={containerRef}
      w="full"
      mx="auto"
      pb="8"
      mt={doesFormHasAnyError ? '4' : '0'}
      px={{ base: 0, md: 5 }}
      ml={{ base: 0, md: 1 }}>
      {/* Para que, quando houverem muitos ingressos, sempre volte no topo */}
      <ScrollToTop />
      <Text wordBreak="break-word" m="0" as="h1">
        {event?.name ?? ''}
      </Text>
      <OwnersSection buyerInfo={buyerInfo ? buyerInfo : undefined} />
      <Text as="h2" mt="10">
        {checkoutBag.totalTickets > 0 ? 'Forma de Pagamento' : 'Resumo'}
      </Text>
      {checkoutBag.totalTickets > 0 && (
        <>
          <Divider border="1px" borderColor="gray.50" />
          <PaymentMethodButton
            eventId={eventID!}
            taxCCPixDiff={event?.ticketTypes[0].taxCCPixDiff ?? 0}
          />
          {formValues.selectedPaymentType === PaymentType.CreditCard && (
            <>
              <Divider border="1px" borderColor="gray.50" />

              <>
                <FloatingLabelContainer mb="2">
                  <FloatingLabelInputNumberFormat
                    format={
                      formValues.selectedCardBrand === CreditCardBrand.Amex
                        ? '####'
                        : '###'
                    }
                    allowNegative={false}
                    allowLeadingZeros={false}
                    decimalScale={0}
                    value={formValues.selectedCardCvv}
                    type="tel"
                    onChange={e => {
                      const currentTarget = e.currentTarget;
                      const cvv = currentTarget.value;
                      setFormValues({ selectedCardCvv: cvv });

                      const hasFullCvv = cvv.length >= 3;
                      if (getErrorKey('selectedCardCvv') && hasFullCvv) {
                        setInvalidFieldsNames?.(prev =>
                          prev.filter(
                            fieldName => fieldName !== 'selectedCardCvv',
                          ),
                        );
                      }
                    }}
                  />
                  <FloatingLabel>Código de segurança</FloatingLabel>
                </FloatingLabelContainer>
                {getErrorKey('selectedCardCvv') && (
                  <ErrorMessage mb="4">CVV Inválido</ErrorMessage>
                )}
              </>
              <InstallmentsField
                isLoading={
                  registerBuyQuery.isPending ||
                  registerBuyQuery.isLoading ||
                  registerBuyQuery.isFetching
                }
                data={registerBuyQuery.data}
                onChange={value => {
                  setTotalAmount(value.total);
                }}
              />
            </>
          )}
          {formValues.selectedPaymentType === PaymentType.Pix && (
            <>
              {(!!formValues.isDocumentRequired ||
                !!formValues.isPhoneRequired) && (
                <Divider border="1px" borderColor="gray.50" />
              )}
              {!!formValues.isDocumentRequired && (
                <>
                  <FloatingLabelContainer
                    mb="2"
                    borderColor={
                      getErrorKey('payerCpf') ? 'red.500' : 'gray.500'
                    }
                    border={getErrorKey('payerCpf') ? '2px' : '1px'} // Borda vermelha em caso de erro
                  >
                    <FloatingLabelInputNumberFormat
                      format="###.###.###-##"
                      value={formValues.payerCpf}
                      onBlur={e => {
                        const typedCpf = e.target.value;
                        const isCpfValid = isValidCpf(typedCpf);

                        if (!isCpfValid) {
                          setInvalidFieldsNames?.(() => ['payerCpf']);
                        } else {
                          setInvalidFieldsNames?.(prev =>
                            prev.filter(fieldName => fieldName !== 'payerCpf'),
                          );
                        }
                      }}
                      onChange={e => {
                        const currentTarget = e.currentTarget;
                        const typedCpf = currentTarget.value;
                        setFormValues({ payerCpf: typedCpf });
                      }}
                    />
                    <FloatingLabel>CPF</FloatingLabel>
                  </FloatingLabelContainer>

                  {getErrorKey('payerCpf') && (
                    <ErrorMessage my="2">CPF Inválido</ErrorMessage>
                  )}
                </>
              )}
              {!!formValues.isPhoneRequired && (
                <>
                  <FloatingLabelContainer
                    borderColor={
                      getErrorKey('payerPhone') ? 'red.500' : 'gray.500'
                    }
                    border={getErrorKey('payerPhone') ? '2px' : '1px'} // Borda vermelha em caso de erro
                  >
                    <FloatingLabelInputNumberFormat
                      format="(##) #####-####"
                      value={formValues.payerPhone}
                      onBlur={e => {
                        const phone = e.target.value;
                        const isPhoneValid = isValidPhone(phone);

                        if (!isPhoneValid) {
                          setInvalidFieldsNames?.(() => ['payerPhone']);
                        } else {
                          setInvalidFieldsNames?.(prev =>
                            prev.filter(
                              fieldName => fieldName !== 'payerPhone',
                            ),
                          );
                        }
                      }}
                      onChange={e => {
                        const currentTarget = e.currentTarget;
                        const phone = currentTarget.value;
                        setFormValues({ payerPhone: phone });
                      }}
                    />
                    <FloatingLabel>Celular</FloatingLabel>
                  </FloatingLabelContainer>
                  {getErrorKey('payerPhone') && (
                    <ErrorMessage my="2">Telefone Inválido</ErrorMessage>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
      <Divider border="1px" borderColor="gray.50" mb="4" />
      <CheckoutSummary
        total={totalInstallmentAmount}
        totalTickets={checkoutBag.totalTickets}
        totalCreditCardFee={checkoutBag.totalCreditCardFee}
        totalPixFee={registerBuyQuery.data?.feePricePix ?? 0}
        totalDiscount={checkoutBag.totalDiscount}
        isCouponApplied={checkoutBag.isCouponApplied}
      />
      {/* {invalidFieldsMessage[0] !== '' && (
        <Text mt="4" color="red.500" textAlign="center">
          {invalidFieldsMessage[0]}
        </Text>
      )} */}
    </Box>
  );
};

export default CheckoutPayment;
